import { assertInInjectionContext, inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { AuthState } from '@tsin-core/states/auth.state';
import { RouterStateSnapshot, ActivatedRouteSnapshot } from '@angular/router';
import { ConfigService } from '@tsin-core/services/common/config.service';

/**
 * Auth guard for WWUS frontend.
 *
 * In staging/production:
 *  - If NGXS auth state has a token → allow (with role-based routing).
 *  - If an `id_token` shared cookie exists (set by auth-ui) → redirect to
 *    /auth/callback so the token-exchange flow can run.
 *  - Otherwise → redirect to the centralized auth-ui login page.
 *
 * In dev (localhost):
 *  - Falls back to the local /account/validate-login route.
 */
const canActivateFn: CanActivateFn = (route, state) => {
  assertInInjectionContext(canActivateFn);
  const router: Router = inject(Router);
  const store: Store = inject(Store);
  const configService: ConfigService = inject(ConfigService);
  const isLogin = store.selectSnapshot(AuthState.getIsAuthenticated);
  console.info(`isLogin ${isLogin}, will activate`);

  const user = store.selectSnapshot(AuthState.getCurrentUser);
  const targetUrl = state.url;

  const roleRouteMap: Record<string, string> = {
    hrAdmin: '/management',
    facilitatorAdmin: '/management',
    superAdmin: '/management',
    admin: '/managers',
    financeAdmin: '/finance',
    financeHeadAdmin: '/finance',
    itAdmin: '/IT'
  };

  // If authenticated, apply role-based routing
  if (isLogin) {
    const roleHome = roleRouteMap[user?.role as string];
    if (roleHome) {
      if (targetUrl.startsWith(roleHome) || targetUrl.startsWith('/employee')) {
        return true;
      }
      return router.parseUrl(roleHome);
    }
    return true;
  }

  // Not authenticated — check for SSO
  const env = configService.getEnvironment();
  const authUiUrl = configService.getAuthUiUrl();

  if (env !== 'dev' && authUiUrl) {
    const idTokenCookie = getCookie('id_token');
    if (idTokenCookie) {
      console.log('🔑 id_token cookie found – redirecting to /auth/callback...');
      return router.parseUrl('/auth/callback');
    }

    const currentUrl = window.location.origin + state.url;
    const loginUrl = `${authUiUrl}/login?returnTo=${encodeURIComponent(currentUrl)}`;
    console.log('🔐 Not authenticated – redirecting to auth-ui:', loginUrl);
    window.location.href = loginUrl;
    return false;
  }

  return router.parseUrl('/account/validate-login');
};

export const authGuard: CanActivateFn = (route, state) => {
  return canActivateFn(route, state);
};

/** Read a cookie value by name. */
function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

export function getLastUrlSlug(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): string {
  const segments = state.url.split('/').filter((segment) => segment.length > 0);
  return segments.length ? segments[segments.length - 1] : '';
}
